import type { components } from '@/generated/api/schema';
import type { OrganizationCategory } from '@/models/organizationCategory';

/** Organization-type filter alias — the shared OpenAPI enum for the customer-list `type` filter. */
type OrganizationTypeFilter = components['schemas']['OrganizationType'];

/** Organization-state filter alias — the raw wire enum accepted by the `state` query parameter. */
type OrganizationStateFilter = components['schemas']['OrganizationState'];

/** Shared parameter shape used by both audience-specific list query-key factories. */
interface CustomersListKeyParams {
  readonly page: number;
  readonly size: number;
  readonly sort: string;
  readonly search?: string | undefined;
  readonly type?: OrganizationTypeFilter | undefined;
  readonly category?: OrganizationCategory | undefined;
  readonly partnerLabId?: string | undefined;
  readonly state?: OrganizationStateFilter | undefined;
  readonly street?: string | undefined;
}

/**
 * TanStack Query key builders for the `customers` feature.
 *
 * Each audience view (portal, DELABO-lab) has its own top-level key segment to guarantee cache
 * isolation — a portal-view query never cross-talks with a DELABO-lab-view query even when
 * page/size/sort/filter params match. The batch order-count lookup (`POST
 * /api/v1/auftraege/counts`) is keyed separately under `counts`, since it is called once per
 * loaded page with that page's visible organization ids rather than with pagination params.
 */
export const customersKeys = {
  /**
   * Root key for all customer-feature queries — use to invalidate the entire feature cache.
   *
   * This is what the create and update mutations invalidate on success: TanStack Query matches
   * keys by prefix, so this one key already covers `portalAll()`, `delaboLaborAll()`, `countsAll()`
   * and every detail query, without a mutation having to enumerate the audience views. It is
   * deliberately used directly rather than behind a `lists()` alias — the alias returned this very
   * key while its name promised something narrower, which invited a second, redundant
   * `invalidateQueries` call alongside it.
   */
  all: ['customers'] as const,

  /** All portal-view (admin/support) list queries. */
  portalAll: () => [...customersKeys.all, 'portal'] as const,

  /** Portal-view list query keyed by pagination and filter params. */
  portal: (params: CustomersListKeyParams) => [...customersKeys.portalAll(), params] as const,

  /** All DELABO-lab-view list queries. */
  delaboLaborAll: () => [...customersKeys.all, 'delabo-labor'] as const,

  /** DELABO-lab-view list query keyed by pagination and filter params. */
  delaboLabor: (params: CustomersListKeyParams) => [...customersKeys.delaboLaborAll(), params] as const,

  /** All batch order-count queries. */
  countsAll: () => [...customersKeys.all, 'counts'] as const,

  /**
   * Order-count query for a page's visible organization ids. Ids are sorted before entering the
   * key so that two calls for the same set of organizations — regardless of the order the page's
   * rows happened to render in — resolve to the same cache entry instead of refetching.
   */
  counts: (orgIds: readonly string[]) => [...customersKeys.countsAll(), [...orgIds].sort()] as const,

  /**
   * KPI count of open partner-lab assignment requests for the caller's own DELABO lab (US-E10-07,
   * AK-2). Takes no params — the count is scoped to the caller's own lab server-side, never by a
   * client-supplied id — so this is a single, unparameterized cache entry. Both
   * `useOpenAssignmentCount` (the Top-Nav badge query) and the confirm/reject mutations
   * (invalidation target after a successful action) reference this key.
   */
  openAssignmentCount: () => [...customersKeys.all, 'open-assignment-count'] as const,

  /** All detail queries (single-customer GET) across every id. */
  detailAll: () => [...customersKeys.all, 'detail'] as const,

  /** Detail query for one customer id (`GET /api/v1/customers/{customerId}`). */
  detail: (id: string) => [...customersKeys.detailAll(), id] as const,

  /** All user-list queries for the customer organization with this id. */
  usersAll: (id: string) => [...customersKeys.detailAll(), id, 'users'] as const,

  /**
   * Users-list query for one customer, keyed by paging/sort/search/role params (mirrors the
   * `listCustomerUsers` operation's query parameters).
   */
  users: (
    id: string,
    params: { page?: number; size?: number; sort?: string; search?: string; role?: string },
  ) => [...customersKeys.usersAll(id), params] as const,

  /** All order-history queries for the customer organization with this id. */
  auftraegeAll: (id: string) => [...customersKeys.detailAll(), id, 'auftraege'] as const,

  /**
   * Order-history query for one customer, keyed by paging/sort params (mirrors the
   * `listAuftraegeFuerKunde` operation's query parameters).
   */
  auftraege: (
    id: string,
    params: { page?: number; size?: number; sort?: string },
  ) => [...customersKeys.auftraegeAll(id), params] as const,

  /** All duplicate-candidate lookups (`POST /api/v1/customers/duplicate-candidates`). */
  duplicateCandidatesAll: () => [...customersKeys.all, 'duplicate-candidates'] as const,

  /** Duplicate-candidate lookup keyed by the searched name/street/city. */
  duplicateCandidates: (params: { name: string; street?: string | undefined; city: string }) =>
    [...customersKeys.duplicateCandidatesAll(), params] as const,
};
